// src/hooks/useGlobalGroupSelector.js
import { useState, useEffect, useRef } from "react";
import { fetchSalesByPaymentType } from "../services/dataService";
import { extractOptions } from "../pages/Metrics/hooks/useMetricsData";
import { toDayString } from "../utils/periods";

const PENDING_GROUP_KEY = "pendingSelectedGroup";
const GROUP_REQUESTED_EVENT = "selectedGroupRequested";

// Выбрать ресторан из другого места приложения (ссылка из уведомления): применит useGlobalGroupSelector,
// когда список ресторанов загружен — в том числе после смены активного сервера
export const requestSelectedGroup = (group) => {
  localStorage.setItem(PENDING_GROUP_KEY, group);
  window.dispatchEvent(new CustomEvent(GROUP_REQUESTED_EVENT));
};

/**
 * Хук для управления глобальным выбором ресторана (торговое предприятие, см. utils/restaurant).
 * Сохраняет состояние в localStorage и загружает список ресторанов.
 * Автоматически перезагружает группы при переключении активного сервера.
 */
export const useGlobalGroupSelector = () => {
  const [selectedGroup, setSelectedGroupState] = useState(() => {
    // Загружаем из localStorage при инициализации
    const saved = localStorage.getItem("selectedGroup");
    return saved || "Все";
  });

  const [allGroups, setAllGroups] = useState(["Все"]);
  const [activeServerName, setActiveServerName] = useState(null);
  const activeServerIdRef = useRef(null);
  const loadAttemptsRef = useRef(0);
  const groupsLoadedRef = useRef(false);
  const maxLoadAttempts = 5; // Максимальное количество попыток загрузки

  // Функция загрузки групп
  const loadGroups = async (isRetry = false) => {
    const token = localStorage.getItem("token");
    if (!token) {
      // Если токена нет, но это не повторная попытка, попробуем еще раз через небольшую задержку
      if (!isRetry && loadAttemptsRef.current < maxLoadAttempts) {
        loadAttemptsRef.current += 1;
        setTimeout(() => loadGroups(true), 500);
      }
      return;
    }

    try {
      // Получаем активный сервер
      const cachedServer = localStorage.getItem("activeServer");
      if (!cachedServer) {
        // Если сервера нет, но это не повторная попытка, попробуем еще раз
        if (!isRetry && loadAttemptsRef.current < maxLoadAttempts) {
          loadAttemptsRef.current += 1;
          setTimeout(() => loadGroups(true), 500);
          return;
        }
        setActiveServerName(null);
        setAllGroups(["Все"]);
        activeServerIdRef.current = null;
        groupsLoadedRef.current = false;
        return;
      }

      const server = JSON.parse(cachedServer);
      const serverId = server?.id;
      const serverName = server?.server_name || server?.name || null;

      // Сохраняем старый ID сервера для проверки изменения
      const previousServerId = activeServerIdRef.current;
      const serverChanged = previousServerId !== serverId;

      // Проверяем, изменился ли сервер
      if (!serverChanged && !isRetry && groupsLoadedRef.current) {
        // Сервер не изменился, группы уже загружены - просто обновляем имя на случай, если оно изменилось
        setActiveServerName(serverName);
        return;
      }

      // Сервер изменился или это повторная попытка - обновляем ref и загружаем группы
      activeServerIdRef.current = serverId;
      setActiveServerName(serverName);

      // Загружаем данные для получения списка групп
      // Используем более широкий диапазон дат (последние 30 дней) для гарантии получения групп
      const today = new Date();
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(today.getDate() - 30);
      
      const dateRange = {
        from: toDayString(thirtyDaysAgo),
        to: toDayString(today),
      };

      const data = await fetchSalesByPaymentType(token, server, dateRange);
      const { groups } = extractOptions(data);
      
      // Сбрасываем счетчик попыток при успешной загрузке
      loadAttemptsRef.current = 0;
      
      if (groups && groups.length > 0) {
        setAllGroups(["Все", ...groups]);
        groupsLoadedRef.current = true;
      } else {
        // Если групп не найдено, попробуем еще раз с текущим месяцем
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const monthDateRange = {
          from: toDayString(monthStart),
          to: toDayString(today),
        };
        try {
          const monthData = await fetchSalesByPaymentType(token, server, monthDateRange);
          const { groups: monthGroups } = extractOptions(monthData);
          if (monthGroups && monthGroups.length > 0) {
            setAllGroups(["Все", ...monthGroups]);
            groupsLoadedRef.current = true;
          } else {
            setAllGroups(["Все"]);
            groupsLoadedRef.current = false;
          }
        } catch (e) {
          setAllGroups(["Все"]);
          groupsLoadedRef.current = false;
        }
      }
      
      // Сбрасываем выбранную группу на "Все" только при переключении сервера (не при повторных попытках)
      if (!isRetry && serverChanged) {
        setSelectedGroupState("Все");
        localStorage.setItem("selectedGroup", "Все");
      }
    } catch (error) {
      console.error("Ошибка загрузки групп:", error);
      // Если это не последняя попытка, попробуем еще раз
      if (loadAttemptsRef.current < maxLoadAttempts) {
        loadAttemptsRef.current += 1;
        setTimeout(() => loadGroups(true), 1000 * loadAttemptsRef.current); // Увеличиваем задержку с каждой попыткой
      } else {
        setAllGroups(["Все"]);
        groupsLoadedRef.current = false;
      }
    }
  };

  // Загружаем список групп при монтировании
  useEffect(() => {
    loadGroups();
  }, []);

  // Отслеживаем изменения activeServer в localStorage
  useEffect(() => {
    // Обработчик для storage события (работает между вкладками)
    const handleStorageChange = (e) => {
      if (e.key === "activeServer") {
        loadAttemptsRef.current = 0; // Сбрасываем счетчик при изменении сервера
        groupsLoadedRef.current = false; // Сбрасываем флаг загрузки
        loadGroups();
      }
      if (e.key === "token") {
        // Если токен изменился, попробуем загрузить группы
        loadAttemptsRef.current = 0;
        groupsLoadedRef.current = false;
        loadGroups();
      }
    };

    // Обработчик для кастомного события (для той же вкладки)
    const handleActiveServerChange = () => {
      loadAttemptsRef.current = 0;
      groupsLoadedRef.current = false;
      loadGroups();
    };

    // Подписываемся на события
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("activeServerChanged", handleActiveServerChange);

    // Проверяем изменения периодически (каждые 3 секунды) как резервный механизм
    // Останавливается автоматически, когда группы загружены
    // Основной механизм - кастомное событие activeServerChanged
    const intervalId = setInterval(() => {
      // Если группы уже загружены, прекращаем периодическую проверку
      if (groupsLoadedRef.current) {
        clearInterval(intervalId);
        return;
      }

      const cachedServer = localStorage.getItem("activeServer");
      const token = localStorage.getItem("token");
      
      // Если есть токен и сервер, но группы еще не загружены, попробуем загрузить
      if (token && cachedServer && !groupsLoadedRef.current && loadAttemptsRef.current < maxLoadAttempts) {
        loadGroups(true);
        return;
      }
      
      // Проверяем изменение сервера только если группы еще не загружены
      if (cachedServer) {
        try {
          const server = JSON.parse(cachedServer);
          const serverId = server?.id;
          if (activeServerIdRef.current !== serverId) {
            loadAttemptsRef.current = 0;
            groupsLoadedRef.current = false;
            loadGroups();
          }
        } catch (e) {
          // Игнорируем ошибки парсинга
        }
      } else if (activeServerIdRef.current !== null) {
        // Сервер был удален
        loadAttemptsRef.current = 0;
        groupsLoadedRef.current = false;
        loadGroups();
      }
    }, 3000); // Увеличено до 3 секунд для снижения нагрузки

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("activeServerChanged", handleActiveServerChange);
      clearInterval(intervalId);
    };
  }, []);

  // Выбор, запрошенный ссылкой (сводка после закрытия смены → «Открыть выручку»): requestSelectedGroup
  // кладёт его в localStorage. Применяется, когда список ресторанов загружен (после смены сервера — новый);
  // такого ресторана в списке нет — запрос отбрасывается
  const allGroupsRef = useRef(allGroups);
  const applyPendingGroup = () => {
    const pending = localStorage.getItem(PENDING_GROUP_KEY);
    if (!pending || !groupsLoadedRef.current) return;
    localStorage.removeItem(PENDING_GROUP_KEY);
    if (allGroupsRef.current.includes(pending)) {
      setSelectedGroupState(pending);
      localStorage.setItem("selectedGroup", pending);
    }
  };

  useEffect(() => {
    allGroupsRef.current = allGroups;
    applyPendingGroup();
  }, [allGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    window.addEventListener(GROUP_REQUESTED_EVENT, applyPendingGroup);
    return () => window.removeEventListener(GROUP_REQUESTED_EVENT, applyPendingGroup);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Сохранённый выбор, которого нет в загруженном списке (ресторан закрылся, другой сервер,
  // или старое значение — раньше здесь были группы RestorauntGroup), — на «Все»
  useEffect(() => {
    if (!groupsLoadedRef.current || allGroups.includes(selectedGroup)) return;
    setSelectedGroupState("Все");
    localStorage.setItem("selectedGroup", "Все");
  }, [allGroups, selectedGroup]);

  // Обновляем selectedGroup и сохраняем в localStorage
  const setSelectedGroup = (group) => {
    setSelectedGroupState(group);
    localStorage.setItem("selectedGroup", group);
  };

  return {
    selectedGroup,
    setSelectedGroup,
    allGroups,
    activeServerName,
  };
};

